import React, { useEffect, useState } from 'react';
import { Check} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { GenerateRuleStep } from './Generate';
import { MapVariablesStep } from './RuleMapping';
import { useAttributes, useCreateAIRule, useDataTypes } from '@/hooks/useLocalStorage';
import { Code, Type } from '@/shared/schema';
import { TestRuleStep } from './TestRule';
import { SaveRuleStep } from './SaveRule';
import { parseApiErrorMessage } from '@/hooks/parseApiErrorMessage';
import { ConfirmationDialog } from '@/widgets/AlertCommon';

type Step = 'generate' | 'map' | 'test' | 'save';
type TokenType = 'datatype-attribute' | 'operand' | 'logical-operator';

export interface VariablePair {
    id: string;
    type: TokenType;
    dataType?: string;
    attribute?: string;
    placeholder: string;
    mappedDataType?: string | null;
    mappedAttribute?: string | null;
    operand?: string;
    mappedOperand?: string | null;
    logicalOperator?: string;
    mappedLogicalOperator?: string | null;
}

export interface MappedPair {
    dataType: string | null;
    attribute: string | null;
}

export default function CreateRulePrompt() {
    const [currentStep, setCurrentStep] = useState<Step>('generate');
    const [generatedRule, setGeneratedRule] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);

    const [variablePairs, setVariablePairs] = useState<VariablePair[]>([]);
    const [currentPairIndex, setCurrentPairIndex] = useState(0);
    const [attrCode, setAttrCode] = useState<string>("");
    const [dataTypeOptions, setDataTypeOptions] = useState<Type[]>([]);
    const [attributeOptions, setAttributeOptions] = useState<Code[]>([]);

    const [testResult, setTestResult] = useState<{ success?: boolean; message: string; details: string }>({ message: "", details: "" });
    const [isSaving, setIsSaving] = useState(false);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");

    const { data: existingDtValues } = useDataTypes(1, undefined, undefined);
    const { data: existingAttribute } = useAttributes(attrCode);
    const createAIRuleMutation = useCreateAIRule();

    useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setDataTypeOptions(existingDtValues.items);
        }
    }, [existingDtValues]);

    useEffect(() => {
        if (existingAttribute && existingAttribute.length > 0) {
            setAttributeOptions(existingAttribute);
        } else {
            setAttributeOptions([]);
        }
    }, [existingAttribute]);

    useEffect(() => {
        const saved = sessionStorage.getItem("generatedRule");
        if (saved) setGeneratedRule(saved);
    }, []);

    useEffect(() => {
        if (generatedRule && currentStep === 'map') {
            const pairs: VariablePair[] = [];
            let id = 0;

            const dtAttrRegex = /#([^#]+)#\.\[([^\]]+)\]/g;
            let match;
            while ((match = dtAttrRegex.exec(generatedRule)) !== null) {
                pairs.push({
                    id: `pair-${id++}`,
                    type: 'datatype-attribute',
                    dataType: match[1],
                    attribute: match[2],
                    placeholder: match[0],
                    mappedDataType: null,
                    mappedAttribute: null
                });
            }

            const operandRegex = /@([^@]+)@/g;
            while ((match = operandRegex.exec(generatedRule)) !== null) {
                pairs.push({
                    id: `pair-${id++}`,
                    type: 'operand',
                    operand: match[1],
                    placeholder: match[0],
                    mappedOperand: null
                });
            }

            const logicalRegex = /\$([^$]+)\$/g;
            while ((match = logicalRegex.exec(generatedRule)) !== null) {
                pairs.push({
                    id: `pair-${id++}`,
                    type: 'logical-operator',
                    logicalOperator: match[1],
                    placeholder: match[0],
                    mappedLogicalOperator: null
                });
            }

            pairs.sort((a, b) => {
                return generatedRule.indexOf(a.placeholder) - generatedRule.indexOf(b.placeholder);
            });

            setVariablePairs(pairs);
        }
    }, [generatedRule, currentStep]);

    const getFinalRule = () => {
        if (!generatedRule) return "";

        let final = generatedRule;
        variablePairs.forEach((pair) => {
            if (pair.type === 'datatype-attribute' && pair.mappedDataType && pair.mappedAttribute) {
                final = final.replace(pair.placeholder, `${pair.mappedDataType}.${pair.mappedAttribute}`);
            } else if (pair.type === 'operand' && pair.mappedOperand) {
                final = final.replace(pair.placeholder, pair.mappedOperand);
            } else if (pair.type === 'logical-operator' && pair.mappedLogicalOperator) {
                final = final.replace(pair.placeholder, pair.mappedLogicalOperator);
            }
        });
        return final;
    };

    const updateVariablePair = (index: number, updates: Partial<VariablePair>) => {
        setVariablePairs(prev => prev.map((pair, i) =>
            i === index ? { ...pair, ...updates } : pair
        ));
    };

    const allPairsCompleted = variablePairs.every(pair => {
        if (pair.type === 'datatype-attribute') {
            return pair.mappedDataType && pair.mappedAttribute;
        } else if (pair.type === 'operand') {
            return pair.mappedOperand;
        } else if (pair.type === 'logical-operator') {
            return pair.mappedLogicalOperator;
        }
        return false;
    });

    const handleSaveRule = async (data: any) => {
        setIsSaving(true);
        createAIRuleMutation.mutateAsync(data);
    };

    const handleStartOver = () => {
        setCurrentStep('generate');
        sessionStorage.removeItem("rule_chat_history");
        sessionStorage.removeItem("rule_session_id");
        sessionStorage.removeItem("generatedRule");
        localStorage.removeItem("selectedRuleAI");
        localStorage.removeItem("selectedRule");
        localStorage.removeItem("selectedVersionRule");
        setGeneratedRule(null);
        setVariablePairs([]);
        setCurrentPairIndex(0);
        setTestResult({ message: "", details: "" });
        setShowResetDialog(false);
    };

    const stepConfig = {
        generate: { title: 'Generate Rule', number: 1 },
        map: { title: 'Map Variables', number: 2 },
        test: { title: 'Test Final Rule', number: 3 },
        save: { title: 'Save', number: 4 }
    };

    React.useEffect(() => {
        if (createAIRuleMutation.isSuccess === true) {
            setAlertMessage(`Rule Created Successfully!`);
            setAlertOpen(true);
            setIsSaving(false);
            createAIRuleMutation.reset();
        } else if (createAIRuleMutation.isError) {
            const raw = createAIRuleMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            setIsSaving(false);
            createAIRuleMutation.reset();
        }
    }, [createAIRuleMutation]);

    const closeSave = () => {
        handleStartOver();
        setAlertOpen(false)
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="space-y-6">
                <div className="text-left space-y-2">
                    <h1 className="text-xl md:text-xl font-semibold text-gray-900">AI Rule Builder</h1>
                    <p className="text-muted-foreground">Create intelligent rules with natural language</p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 md:gap-6">
                    <div className="xl:col-span-3 space-y-6">

                        <div className="min-h-[400px]">
                            {currentStep === 'generate' && (
                                <GenerateRuleStep
                                    onNext={() => setCurrentStep('map')}
                                    generatedRule={generatedRule}
                                    setGeneratedRule={setGeneratedRule}
                                    isGenerating={isGenerating}
                                    setIsGenerating={setIsGenerating}
                                />
                            )}

                            {currentStep === 'map' && (
                                <MapVariablesStep
                                    variablePairs={variablePairs}
                                    currentPairIndex={currentPairIndex}
                                    setCurrentPairIndex={setCurrentPairIndex}
                                    updateVariablePair={updateVariablePair}
                                    dataTypeOptions={dataTypeOptions}
                                    attributeOptions={attributeOptions}
                                    setAttrCode={setAttrCode}
                                    onBack={() => setCurrentStep('generate')}
                                    onNext={() => setCurrentStep('test')}
                                    generatedRule={generatedRule}
                                />
                            )}

                            {currentStep === 'test' && (
                                <TestRuleStep
                                    finalRule={getFinalRule()}
                                    onBack={() => setCurrentStep('map')}
                                    onNext={() => setCurrentStep('save')}
                                    testResult={testResult}
                                    setTestResult={setTestResult}
                                    variablePairs={variablePairs}
                                />
                            )}

                            {currentStep === 'save' && (
                                <SaveRuleStep
                                    onBack={() => setCurrentStep('test')}
                                    finalRule={getFinalRule()}
                                    onSave={handleSaveRule}
                                    isSaving={isSaving}
                                />
                            )}


                        </div>
                        {currentStep !== 'generate' && (
                            <div className="flex justify-end">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowResetDialog(true)}
                                    className="text-muted-foreground"
                                >
                                    Start Over
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="xl:col-span-1">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="relative flex flex-col">

                                    <div className="absolute left-5 top-0 bottom-0 w-[10px] h-[480px] bg-slate-200" />

                                    {Object.entries(stepConfig).map(([key, config], idx) => {
                                        const isCompleted = Object.keys(stepConfig).indexOf(currentStep) > idx;
                                        const isCurrent = currentStep === key;

                                        return (
                                            <div key={key} className="relative flex items-center gap-4 py-9">

                                                <div
                                                    className={`
                                                                 z-10 w-12 h-12 rounded-full flex items-center justify-center font-semibold
                                                                 ${isCurrent ? 'bg-primary text-primary-foreground' :
                                                            isCompleted ? 'bg-green-500 text-white' :
                                                                'bg-slate-200 text-slate-600'}
                                                             `}
                                                >
                                                    {isCompleted ? <Check className="h-5 w-5" /> : config.number}
                                                </div>

                                                <span className="text-md font-medium leading-none">
                                                    {config.title}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </div>


                </div>

                <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Start Over?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will discard all your current progress and reset the wizard. Are you sure?
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleStartOver}>
                                Yes, Start Over
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
            <ConfirmationDialog
                open={alertOpen}
                onOpenChange={() => setAlertOpen(false)}
                onConfirm={closeSave}
                message={alertMessage}
                heading={createAIRuleMutation.isSuccess === true ? "Success" : "Save Rule Failed"}
            />
        </div>
    );
}